import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Database, LayoutDashboard } from "lucide-react";
import { ModuleFeatureCard } from "@/components/HubModuleCard";
import ModulePlanBlockedDialog from "@/components/ModulePlanBlockedDialog";
import { resolveModuleNavIcon } from "@/modules/moduleNavIcons";
import { useEntitlements } from "@/modules/useEntitlements";
import { usePlatformDesigner } from "@/lib/usePlatformDesigner";
import {
  DATOS_MAESTROS_CARDS,
  filterDatosMaestrosCards,
  groupDatosMaestrosCards,
} from "@/modules/datosMaestros/datosMaestrosHub";
import {
  DATOS_MAESTROS_PANEL_TAB,
  DATOS_MAESTROS_PATH,
  buildDatosMaestrosCardNavigationState,
  buildDatosMaestrosCardPathWithReturn,
} from "@/modules/datosMaestros/datosMaestrosNavigation";
import { applyDatosMaestrosWorkModule } from "@/modules/datosMaestros/applyDatosMaestrosWorkModule";

/**
 * Cuerpo del hub Datos Maestros — tarjetas por entitlement.
 * Reutilizable en /datos-maestros y en la pestaña del Panel General.
 *
 * @param {{ embedded?: boolean }} props
 *   embedded: sin bloque introductorio (Panel General ya tiene contexto en la pestaña).
 */
export default function DatosMaestrosHubContent({ embedded = false } = {}) {
  const navigate = useNavigate();
  const isPlatformDesigner = usePlatformDesigner();
  const {
    loading: permLoading,
    isPlatformDesigner: entitlementDesignerBypass,
    entitlements,
    modulo_visor_sat,
    modulo_asistente_laboral,
    modulo_tramites_fiscales,
    modulo_gestion_despacho,
  } = useEntitlements();

  const [planBlockedOpen, setPlanBlockedOpen] = useState(false);

  const entitlementsState = useMemo(
    () => ({
      entitlements,
      modulo_visor_sat,
      modulo_asistente_laboral,
      modulo_tramites_fiscales,
      modulo_gestion_despacho,
    }),
    [
      entitlements,
      modulo_visor_sat,
      modulo_asistente_laboral,
      modulo_tramites_fiscales,
      modulo_gestion_despacho,
    ],
  );

  const visibleCards = useMemo(
    () =>
      filterDatosMaestrosCards(DATOS_MAESTROS_CARDS, entitlementsState, {
        isPlatformDesigner: isPlatformDesigner || entitlementDesignerBypass,
      }),
    [entitlementsState, isPlatformDesigner, entitlementDesignerBypass],
  );

  const grouped = useMemo(() => groupDatosMaestrosCards(visibleCards), [visibleCards]);

  const cardHasAccess = (card) =>
    filterDatosMaestrosCards([card], entitlementsState, {
      isPlatformDesigner: isPlatformDesigner || entitlementDesignerBypass,
    }).length > 0;

  const openCard = (card) => {
    if (permLoading) return;
    if (!cardHasAccess(card)) {
      setPlanBlockedOpen(true);
      return;
    }
    const cardPath = String(card.path || "").split("?")[0];
    applyDatosMaestrosWorkModule(cardPath);
    const returnTo = embedded ? DATOS_MAESTROS_PANEL_TAB : DATOS_MAESTROS_PATH;
    navigate(buildDatosMaestrosCardPathWithReturn(card.path, returnTo), {
      state: buildDatosMaestrosCardNavigationState(returnTo),
    });
  };

  return (
    <>
      <div
        className={embedded ? "space-y-6" : "p-8 max-w-6xl space-y-8"}
        data-testid={embedded ? "panel-datos-maestros-content" : "datos-maestros-hub"}
      >
        {!embedded ? (
          <div className="rounded-md border border-slate-200 bg-white shadow-sm p-5 flex items-start gap-3">
            <div className="rounded-md bg-[#0F2D5C]/10 p-2 shrink-0">
              <Database className="w-5 h-5 text-[#0F2D5C]" strokeWidth={1.8} />
            </div>
            <div className="text-sm text-slate-700 leading-relaxed max-w-3xl">
              <p>
                Punto de entrada transversal al catálogo de empresas/contribuyentes y a las pantallas SAT
                de documentos e integración. Las rutas operativas de cada módulo siguen disponibles; aquí
                se centraliza el acceso común sin duplicar datos ni APIs.
              </p>
            </div>
          </div>
        ) : null}

        {grouped.map(({ key, label, items }) => (
          <section key={key || "__default__"} aria-label={label || "Datos maestros"}>
            {label ? (
              <h2 className="text-xs font-bold uppercase tracking-wider text-[#0F2D5C] mb-3">{label}</h2>
            ) : null}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {items.map((card) => {
                const Icon = resolveModuleNavIcon(card.iconKey, LayoutDashboard);
                const hasAccess = cardHasAccess(card);
                return (
                  <ModuleFeatureCard
                    key={card.id}
                    label={card.label}
                    description={card.description}
                    icon={Icon}
                    hasModuleAccess={hasAccess}
                    accessLoading={permLoading}
                    testId={card.testid}
                    cardStatus={card.status}
                    onEnter={() => openCard(card)}
                  />
                );
              })}
            </div>
          </section>
        ))}

        {!permLoading && visibleCards.length === 0 ? (
          <p className="text-sm text-slate-600" data-testid="datos-maestros-empty">
            No hay funciones de datos maestros disponibles con su plan actual. Revise licencia o módulos
            contratados en Mi Negocio / Licencia.
          </p>
        ) : null}
      </div>

      <ModulePlanBlockedDialog open={planBlockedOpen} onOpenChange={setPlanBlockedOpen} />
    </>
  );
}
